import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, KeyRound, LogIn, Menu, User, X } from 'lucide-react';
import { SITE_MENU_CSS } from '../constants/siteMenuWidget';

const LOGIN_ICONS = { LogIn, User, KeyRound };

/**
 * Palco do Menu Site. O fixo real só entra na visualização e no HTML publicado,
 * para a barra não cobrir o editor.
 */
export default function RenderSiteMenu({ content = {}, fixedHint = 'Fixo no topo da página publicada' }) {
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [stageBleed, setStageBleed] = useState(null);
  const slotRef = useRef(null);
  const widthMode = content.widthMode === 'boxed' ? 'boxed' : 'full';
  useLayoutEffect(() => {
    if (widthMode !== 'full') {
      setStageBleed(null);
      return undefined;
    }
    const slot = slotRef.current;
    const host = slot?.parentElement;
    const canvas = slot?.closest('.geppetto-edit-stage-outer') || slot?.closest('.course-wrapper');
    if (!host || !canvas) return undefined;
    const measure = () => {
      const hostBox = host.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      setStageBleed({
        width: canvasBox.width,
        marginLeft: canvasBox.left - hostBox.left,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [widthMode]);
  const styleName = ['classic', 'underline', 'pills', 'transparent'].includes(content.menuStyle) ? content.menuStyle : 'classic';
  const LoginIcon = LOGIN_ICONS[content.loginIcon] || LogIn;
  const items = Array.isArray(content.items) ? content.items : [];
  const logoUrl = String(content.logoUrl || '').trim();
  const vars = {
    '--sm-bg': content.barBg || '#ffffff',
    '--sm-link': content.linkColor || '#0f172a',
    '--sm-hover': content.linkHoverColor || '#2563eb',
    '--sm-login-bg': content.loginBg || '#2563eb',
    '--sm-login': content.loginColor || '#ffffff',
    '--sm-size': `${Number(content.fontSize) || 15}px`,
    '--sm-logo-h': `${Number(content.logoHeight) || 40}px`,
    '--sm-slot': `${Math.max(56, (Number(content.logoHeight) || 40) + 32)}px`,
  };
  const classes = [
    'madri-site-menu',
    widthMode === 'boxed' ? 'madri-site-menu--boxed' : 'madri-site-menu--stage-full',
    `madri-site-menu--${styleName}`,
    styleName !== 'transparent' ? 'madri-site-menu--shadow' : '',
    open ? 'is-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={slotRef} className="madri-site-menu-slot" style={{ position: 'relative' }}>
      <style>{SITE_MENU_CSS}</style>
      {content.behavior === 'fixed' && (
        <div style={{ position: 'absolute', top: 6, right: 8, zIndex: 2, fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', borderRadius: 999, padding: '2px 8px' }}>
          {fixedHint}
        </div>
      )}
      <header
        className={classes}
        style={{
          ...vars,
          ...(stageBleed ? { width: stageBleed.width, maxWidth: 'none', marginLeft: stageBleed.marginLeft, marginRight: 0 } : null),
        }}
      >
        <div className="madri-site-menu__bar">
          {content.logoHref ? (
            <a className="madri-site-menu__logo" href={content.logoHref} onClick={(e) => e.preventDefault()}>
              {logoUrl ? <img src={logoUrl} alt={content.logoAlt || content.logoText || 'Logo'} /> : (content.logoText || 'Logo')}
            </a>
          ) : (
            <span className="madri-site-menu__logo">
              {logoUrl ? <img src={logoUrl} alt={content.logoAlt || content.logoText || 'Logo'} /> : (content.logoText || 'Logo')}
            </span>
          )}
          <nav className="madri-site-menu__nav" aria-label="Menu">
            <ul className="madri-site-menu__list">
              {items.filter((item) => String(item?.label || '').trim()).map((item, index) => {
                const children = (Array.isArray(item.children) ? item.children : []).filter((child) => String(child?.label || '').trim());
                return (
                  <li key={`${item.label}-${index}`} className={`madri-site-menu__item${children.length ? ' has-sub' : ''}${openSub === index ? ' is-open' : ''}`}>
                    <a className="madri-site-menu__link" href={item.href || '#'} onClick={(e) => e.preventDefault()}>{item.label}</a>
                    {children.length > 0 && (
                      <>
                        <button type="button" className="madri-site-menu__caret" aria-label={item.label} onClick={() => setOpenSub(openSub === index ? null : index)}>
                          <ChevronDown size={14} />
                        </button>
                        <ul className="madri-site-menu__sub">
                          {children.map((child, childIndex) => (
                            <li key={`${child.label}-${childIndex}`}>
                              <a href={child.href || '#'} onClick={(e) => e.preventDefault()}>{child.label}</a>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
          {content.loginEnabled !== false && (
            <a className="madri-site-menu__login" href={content.loginHref || '#'} onClick={(e) => e.preventDefault()}>
              <LoginIcon size={18} />
              <span>{content.loginLabel || 'Entrar'}</span>
            </a>
          )}
          <button type="button" className="madri-site-menu__toggle" aria-expanded={open} aria-label="Menu" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>
    </div>
  );
}
